import clsx from 'clsx';
import { useCallback, useRef } from 'react';

import { Field, fieldStyles, getFieldIconColor, useFieldId } from '../Field';
import { Icon } from '../Icon';
import { Typography } from '../typography';

import type { FieldSize, FieldVariant } from '../Field';
import type { DetailedHTMLProps, MouseEvent, ReactNode, SelectHTMLAttributes } from 'react';

import s from './SelectField.module.css';

type SelectFieldSize = FieldSize;
type SelectFieldVariant = FieldVariant;

export type SelectOption = {
  value: string;
  label?: string;
};

type SelectFieldProps = Omit<
  DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>,
  'prefix' | 'size'
> & {
  /** Options for the select */
  options: SelectOption[];
  /** Container className */
  containerClassName?: string;
  /** Make select take full width of its container */
  fullWidth?: boolean;
  /** Helper text displayed below the select */
  hint?: ReactNode;
  /** Label text displayed at the end (shows as placeholder if no label) */
  label?: string;
  /** Placeholder text when no option is selected */
  placeholder?: string;
  /** Icon or element displayed at the start of the select */
  prefix?: ReactNode;
  /** Select className */
  selectClassName?: string;
  /** Size of the select field
   * @default 'default'
   */
  size?: SelectFieldSize;
  /** Element displayed at the end of the select */
  suffix?: ReactNode;
  /** Visual state of the select
   * @default 'default'
   */
  variant?: SelectFieldVariant;
};

export const SelectField = ({
  className,
  containerClassName,
  disabled,
  fullWidth,
  hint,
  id,
  label,
  options,
  placeholder,
  prefix,
  ref,
  required,
  selectClassName,
  size = 'default',
  suffix,
  value,
  variant = 'default',
  ...props
}: SelectFieldProps) => {
  const internalRef = useRef<HTMLSelectElement>(null);

  const setRefs = useCallback(
    (node: HTMLSelectElement | null) => {
      internalRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const selectId = useFieldId(id);

  const hasLabel = Boolean(label);
  const hasPrefix = Boolean(prefix);
  const hasSuffix = Boolean(suffix);

  const placeholderText = placeholder ?? label;

  const iconColor = getFieldIconColor(variant, disabled);
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hintId = hint ? `${selectId}-hint` : undefined;

  // Check if a value is selected (not empty string)
  const hasValue = value !== undefined && value !== '';

  const handleWrapperClick = (e: MouseEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    // Don't trigger if clicking on the select itself (it handles its own click)
    const target = e.target as HTMLElement;
    if (target.tagName === 'SELECT' || target.tagName === 'OPTION') {
      return;
    }

    internalRef.current?.focus();
    internalRef.current?.showPicker?.();
  };

  return (
    <Field className={containerClassName} disabled={disabled} hint={hint} hintId={hintId} variant={variant}>
      <Field.Wrapper
        hasPrefix={hasPrefix}
        disabled={disabled}
        fullWidth={fullWidth}
        hasSuffix={hasSuffix}
        onClick={handleWrapperClick}
        size={size}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        variant={variant}
      >
        <label className={fieldStyles.labelWrapper} htmlFor={selectId}>
          {hasPrefix && <Field.Prefix color={iconColor}>{prefix}</Field.Prefix>}

          <select
            ref={setRefs}
            className={clsx(
              fieldStyles.control,
              fieldStyles[`control--variant-${variant}`],
              selectClassName,
              className,
            )}
            aria-describedby={hintId}
            aria-invalid={variant === 'danger'}
            aria-required={required}
            data-placeholder={!hasValue}
            disabled={disabled}
            id={selectId}
            required={required}
            value={value}
            {...props}
          >
            {placeholderText && (
              <option value="" disabled>
                {placeholderText}
              </option>
            )}

            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label ?? option.value}
              </option>
            ))}
          </select>

          {hasLabel && hasValue && (
            <Typography className={s.label} color={iconColor} component="span" variant="inset-paragraph">
              {label}
            </Typography>
          )}

          <Icon color="color-grey-light" height={21} name="chevron-down" width={21} />

          {hasSuffix && <Field.Suffix color={iconColor}>{suffix}</Field.Suffix>}
        </label>
      </Field.Wrapper>
    </Field>
  );
};
